/**
 * ActionPlanCollaboration.js
 * MEVIA v4.0 - Fase 5: Mejoras a Planes de Acción
 * Componentes para gestión de asignaciones, aprobaciones y evidencias
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Users, UserPlus, UserMinus, Check, X, Clock, 
  FileText, Upload, Trash2, Eye, Download,
  MessageSquare, History, AlertCircle, CheckCircle,
  Send, RefreshCw
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import logger from '../utils/logger';

// ============== ASSIGNEES PANEL ==============
export const AssigneesPanel = ({ itemId, planId, onUpdate }) => {
  const { language } = useLanguage();
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedRole, setSelectedRole] = useState('responsible');

  const labels = {
    es: {
      title: 'Responsables',
      addAssignee: 'Agregar Responsable',
      responsible: 'Responsable',
      collaborator: 'Colaborador',
      reviewer: 'Revisor',
      noAssignees: 'Sin asignaciones',
      selectEmployee: 'Seleccionar empleado',
      selectRole: 'Seleccionar rol',
      add: 'Agregar',
      cancel: 'Cancelar',
      remove: 'Remover',
      assignedAt: 'Asignado el'
    },
    en: {
      title: 'Assignees',
      addAssignee: 'Add Assignee',
      responsible: 'Responsible',
      collaborator: 'Collaborator',
      reviewer: 'Reviewer',
      noAssignees: 'No assignees',
      selectEmployee: 'Select employee',
      selectRole: 'Select role',
      add: 'Add',
      cancel: 'Cancel',
      remove: 'Remove',
      assignedAt: 'Assigned on'
    }
  };
  const t = labels[language] || labels.es;

  const fetchAssignees = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/action-plans-v2/items/${itemId}/assignees`);
      if (response.data.success) {
        setAssignees(response.data.data);
      }
    } catch (error) {
      logger.error('Error fetching assignees:', error);
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get('/companies/employees');
      if (response.data.success && response.data.data) {
        setEmployees(response.data.data);
      }
    } catch (error) {
      logger.error('Error fetching employees:', error);
    }
  };

  useEffect(() => {
    fetchAssignees();
  }, [fetchAssignees]);

  const handleAddAssignee = async () => {
    if (!selectedEmployee) return;
    
    try {
      const response = await axios.post(`/action-plans-v2/items/${itemId}/assignees`, {
        user_id: selectedEmployee,
        role: selectedRole
      });
      
      if (response.data.success) {
        toast.success(language === 'es' ? 'Responsable agregado' : 'Assignee added');
        fetchAssignees();
        setShowAddModal(false);
        setSelectedEmployee('');
        setSelectedRole('responsible');
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleRemoveAssignee = async (assigneeId) => {
    try {
      await axios.delete(`/action-plans-v2/assignees/${assigneeId}`);
      toast.success(language === 'es' ? 'Responsable removido' : 'Assignee removed');
      fetchAssignees();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const roleColors = {
    responsible: 'bg-blue-100 text-blue-800',
    collaborator: 'bg-green-100 text-green-800',
    reviewer: 'bg-purple-100 text-purple-800'
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium flex items-center gap-2">
          <Users className="h-4 w-4" />
          {t.title}
        </h4>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => {
            fetchEmployees();
            setShowAddModal(true);
          }}
        >
          <UserPlus className="h-3 w-3 mr-1" />
          {t.addAssignee}
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Cargando...</div>
      ) : assignees.length === 0 ? (
        <p className="text-sm text-gray-500">{t.noAssignees}</p>
      ) : (
        <div className="space-y-2">
          {assignees.map((assignee) => (
            <div 
              key={assignee.id}
              className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-xs font-medium">
                  {assignee.user_name?.charAt(0) || '?'}
                </div>
                <div>
                  <p className="text-sm font-medium">{assignee.user_name}</p>
                  <p className="text-xs text-gray-500">{assignee.user_email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={roleColors[assignee.role] || 'bg-gray-100'}>
                  {t[assignee.role] || assignee.role}
                </Badge>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => handleRemoveAssignee(assignee.id)}
                >
                  <UserMinus className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Assignee Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.addAssignee}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">{t.selectEmployee}</label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger>
                  <SelectValue placeholder={t.selectEmployee} />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.nombre} ({emp.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">{t.selectRole}</label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="responsible">{t.responsible}</SelectItem>
                  <SelectItem value="collaborator">{t.collaborator}</SelectItem>
                  <SelectItem value="reviewer">{t.reviewer}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              {t.cancel}
            </Button>
            <Button onClick={handleAddAssignee} disabled={!selectedEmployee}>
              {t.add}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== APPROVAL PANEL ==============
export const ApprovalPanel = ({ planId, planStatus, onUpdate }) => {
  const { language } = useLanguage();
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedApprover, setSelectedApprover] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [dueDate, setDueDate] = useState('');

  const labels = {
    es: {
      title: 'Aprobaciones',
      requestApproval: 'Solicitar Aprobación',
      pending: 'Pendiente',
      approved: 'Aprobado',
      rejected: 'Rechazado',
      changesRequested: 'Cambios Solicitados',
      noApprovals: 'Sin solicitudes de aprobación',
      selectApprover: 'Seleccionar aprobador',
      message: 'Mensaje (opcional)',
      dueDate: 'Fecha límite',
      send: 'Enviar Solicitud',
      cancel: 'Cancelar',
      approve: 'Aprobar',
      reject: 'Rechazar',
      requestChanges: 'Solicitar Cambios',
      respondToApproval: 'Responder a Solicitud',
      comments: 'Comentarios'
    },
    en: {
      title: 'Approvals',
      requestApproval: 'Request Approval',
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      changesRequested: 'Changes Requested',
      noApprovals: 'No approval requests',
      selectApprover: 'Select approver',
      message: 'Message (optional)',
      dueDate: 'Due date',
      send: 'Send Request',
      cancel: 'Cancel',
      approve: 'Approve',
      reject: 'Reject',
      requestChanges: 'Request Changes',
      respondToApproval: 'Respond to Request',
      comments: 'Comments'
    }
  };
  const t = labels[language] || labels.es;

  const fetchApprovals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/action-plans-v2/plans/${planId}/approvals`);
      if (response.data.success) {
        setApprovals(response.data.data);
      }
    } catch (error) {
      logger.error('Error fetching approvals:', error);
    } finally {
      setLoading(false);
    }
  }, [planId]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get('/companies/employees');
      if (response.data.success && response.data.data) {
        setEmployees(response.data.data.filter(e => e.rol === 'company_admin'));
      }
    } catch (error) {
      logger.error('Error fetching employees:', error);
    }
  };

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const handleRequestApproval = async () => {
    if (!selectedApprover) return;
    
    try {
      const response = await axios.post(`/action-plans-v2/plans/${planId}/approvals`, {
        approver_id: selectedApprover,
        message: requestMessage || null,
        due_date: dueDate || null
      });
      
      if (response.data.success) {
        toast.success(language === 'es' ? 'Solicitud enviada' : 'Request sent');
        fetchApprovals();
        setShowRequestModal(false);
        setSelectedApprover('');
        setRequestMessage('');
        setDueDate('');
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleRespondToApproval = async (approvalId, action, comments = '') => {
    try {
      const response = await axios.post(`/action-plans-v2/approvals/${approvalId}/respond`, {
        action,
        comments
      });
      
      if (response.data.success) {
        toast.success(language === 'es' ? 'Respuesta enviada' : 'Response sent');
        fetchApprovals();
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    changes_requested: 'bg-orange-100 text-orange-800'
  };

  const statusIcons = {
    pending: Clock,
    approved: CheckCircle,
    rejected: X,
    changes_requested: AlertCircle
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Check className="h-4 w-4" />
            {t.title}
          </CardTitle>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              fetchEmployees();
              setShowRequestModal(true);
            }}
            disabled={planStatus === 'pending_approval'}
          >
            <Send className="h-3 w-3 mr-1" />
            {t.requestApproval}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-gray-500">Cargando...</div>
        ) : approvals.length === 0 ? (
          <p className="text-sm text-gray-500">{t.noApprovals}</p>
        ) : (
          <div className="space-y-3">
            {approvals.map((approval) => {
              const StatusIcon = statusIcons[approval.status] || Clock;
              return (
                <div 
                  key={approval.id}
                  className="p-3 border rounded-lg space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <StatusIcon className="h-4 w-4" />
                      <span className="font-medium">{approval.approver_name}</span>
                    </div>
                    <Badge className={statusColors[approval.status]}>
                      {t[approval.status] || approval.status}
                    </Badge>
                  </div>
                  {approval.request_message && (
                    <p className="text-sm text-gray-600">
                      <strong>{language === 'es' ? 'Solicitud:' : 'Request:'}</strong> {approval.request_message}
                    </p>
                  )}
                  {approval.response_message && (
                    <p className="text-sm text-gray-600">
                      <strong>{language === 'es' ? 'Respuesta:' : 'Response:'}</strong> {approval.response_message}
                    </p>
                  )}
                  <div className="text-xs text-gray-400">
                    {new Date(approval.requested_at).toLocaleDateString()}
                    {approval.due_date && ` • ${language === 'es' ? 'Vence:' : 'Due:'} ${new Date(approval.due_date).toLocaleDateString()}`}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Request Approval Modal */}
      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.requestApproval}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">{t.selectApprover}</label>
              <Select value={selectedApprover} onValueChange={setSelectedApprover}>
                <SelectTrigger>
                  <SelectValue placeholder={t.selectApprover} />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.nombre} ({emp.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">{t.message}</label>
              <Textarea 
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                placeholder={t.message}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t.dueDate}</label>
              <Input 
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestModal(false)}>
              {t.cancel}
            </Button>
            <Button onClick={handleRequestApproval} disabled={!selectedApprover}>
              {t.send}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ============== EVIDENCE PANEL ==============
export const EvidencePanel = ({ planId, itemId, onUpdate }) => {
  const { language } = useLanguage();
  const [evidences, setEvidences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [description, setDescription] = useState('');
  const [evidenceType, setEvidenceType] = useState('document');

  const labels = {
    es: {
      title: 'Evidencias',
      uploadEvidence: 'Subir Evidencia',
      noEvidences: 'Sin evidencias',
      selectFile: 'Seleccionar archivo',
      description: 'Descripción',
      type: 'Tipo',
      document: 'Documento',
      image: 'Imagen',
      link: 'Enlace',
      other: 'Otro',
      upload: 'Subir',
      cancel: 'Cancelar',
      uploadedBy: 'Subido por',
      delete: 'Eliminar'
    },
    en: {
      title: 'Evidence',
      uploadEvidence: 'Upload Evidence',
      noEvidences: 'No evidence',
      selectFile: 'Select file',
      description: 'Description',
      type: 'Type',
      document: 'Document',
      image: 'Image',
      link: 'Link',
      other: 'Other',
      upload: 'Upload',
      cancel: 'Cancel',
      uploadedBy: 'Uploaded by',
      delete: 'Delete'
    }
  };
  const t = labels[language] || labels.es;

  const fetchEvidences = useCallback(async () => {
    try {
      setLoading(true);
      let url = `/action-plans-v2/plans/${planId}/evidences`;
      if (itemId) {
        url += `?item_id=${itemId}`;
      }
      const response = await axios.get(url);
      if (response.data.success) {
        setEvidences(response.data.data);
      }
    } catch (error) {
      logger.error('Error fetching evidences:', error);
    } finally {
      setLoading(false);
    }
  }, [planId, itemId]);

  useEffect(() => {
    fetchEvidences();
  }, [fetchEvidences]);

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('description', description);
      formData.append('evidence_type', evidenceType);
      if (itemId) {
        formData.append('item_id', itemId);
      }

      const response = await axios.post(
        `/action-plans-v2/plans/${planId}/evidences`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      if (response.data.success) {
        toast.success(language === 'es' ? 'Evidencia subida' : 'Evidence uploaded');
        fetchEvidences();
        setShowUploadModal(false);
        setSelectedFile(null);
        setDescription('');
        setEvidenceType('document');
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (evidenceId) => {
    try {
      await axios.delete(`/action-plans-v2/evidences/${evidenceId}`);
      toast.success(language === 'es' ? 'Evidencia eliminada' : 'Evidence deleted');
      fetchEvidences();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const typeIcons = {
    document: FileText,
    image: Eye,
    link: Download,
    other: FileText
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4" />
            {t.title}
          </CardTitle>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowUploadModal(true)}
          >
            <Upload className="h-3 w-3 mr-1" />
            {t.uploadEvidence}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-gray-500">Cargando...</div>
        ) : evidences.length === 0 ? (
          <p className="text-sm text-gray-500">{t.noEvidences}</p>
        ) : (
          <div className="space-y-2">
            {evidences.map((evidence) => {
              const TypeIcon = typeIcons[evidence.evidence_type] || FileText;
              return (
                <div 
                  key={evidence.id}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <TypeIcon className="h-5 w-5 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium">{evidence.file_name}</p>
                      <p className="text-xs text-gray-500">
                        {t.uploadedBy} {evidence.uploaded_by_name} • {new Date(evidence.created_at).toLocaleDateString()}
                      </p>
                      {evidence.description && (
                        <p className="text-xs text-gray-600 mt-1">{evidence.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{t[evidence.evidence_type] || evidence.evidence_type}</Badge>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDelete(evidence.id)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.uploadEvidence}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">{t.selectFile}</label>
              <Input 
                type="file"
                onChange={(e) => setSelectedFile(e.target.files[0])}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t.type}</label>
              <Select value={evidenceType} onValueChange={setEvidenceType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="document">{t.document}</SelectItem>
                  <SelectItem value="image">{t.image}</SelectItem>
                  <SelectItem value="link">{t.link}</SelectItem>
                  <SelectItem value="other">{t.other}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">{t.description}</label>
              <Textarea 
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t.description}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadModal(false)}>
              {t.cancel}
            </Button>
            <Button onClick={handleUpload} disabled={!selectedFile || uploading}>
              {uploading ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : null}
              {t.upload}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ============== HISTORY PANEL ==============
export const HistoryPanel = ({ planId }) => {
  const { language } = useLanguage();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const labels = {
    es: {
      title: 'Historial de Actividad',
      noHistory: 'Sin actividad registrada',
      actions: {
        created: 'Plan creado',
        updated: 'Plan actualizado',
        status_changed: 'Estado cambiado',
        assignee_added: 'Responsable agregado',
        assignee_removed: 'Responsable removido',
        approval_requested: 'Aprobación solicitada',
        approval_approved: 'Plan aprobado',
        approval_rejected: 'Plan rechazado',
        evidence_added: 'Evidencia subida',
        evidence_deleted: 'Evidencia eliminada'
      }
    },
    en: {
      title: 'Activity History',
      noHistory: 'No activity recorded',
      actions: {
        created: 'Plan created',
        updated: 'Plan updated',
        status_changed: 'Status changed',
        assignee_added: 'Assignee added',
        assignee_removed: 'Assignee removed',
        approval_requested: 'Approval requested',
        approval_approved: 'Plan approved',
        approval_rejected: 'Plan rejected',
        evidence_added: 'Evidence uploaded',
        evidence_deleted: 'Evidence deleted'
      }
    }
  };
  const t = labels[language] || labels.es;

  // Función para formatear detalles de forma amigable
  const formatDetails = (action, details) => {
    if (!details || Object.keys(details).length === 0) return null;
    
    switch (action) {
      case 'created':
        return details.employee ? 
          `${language === 'es' ? 'Para:' : 'For:'} ${details.employee}` +
          (details.items_count ? ` (${details.items_count} ${language === 'es' ? 'elementos' : 'items'})` : '') : null;
      
      case 'assignee_added':
        return details.user_name ? 
          `${language === 'es' ? 'Asignado a:' : 'Assigned to:'} ${details.user_name}` : null;
      
      case 'assignee_removed':
        return details.user_name ? 
          `${language === 'es' ? 'Removido:' : 'Removed:'} ${details.user_name}` : null;
      
      case 'status_changed':
        return details.from && details.to ? 
          `${details.from} → ${details.to}` : 
          (details.new_status || details.status || null);
      
      case 'approval_requested':
        return details.approver_name ? 
          `${language === 'es' ? 'Solicitado a:' : 'Requested from:'} ${details.approver_name}` : null;
      
      case 'approval_approved':
      case 'approval_rejected':
        return details.comment ? `"${details.comment}"` : null;
      
      case 'evidence_added':
        return details.filename || details.file_name ? 
          `${language === 'es' ? 'Archivo:' : 'File:'} ${details.filename || details.file_name}` : null;
      
      case 'evidence_deleted':
        return details.filename || details.file_name ? 
          `${language === 'es' ? 'Archivo eliminado:' : 'Deleted file:'} ${details.filename || details.file_name}` : null;
      
      case 'updated':
        if (details.field) {
          return `${details.field}: ${details.old_value || '(vacío)'} → ${details.new_value || '(vacío)'}`;
        }
        return null;
      
      default:
        // Solo mostrar campos relevantes, no IDs técnicos
        const relevantKeys = ['user_name', 'employee', 'comment', 'filename', 'status', 'reason'];
        const relevantDetails = Object.entries(details)
          .filter(([key]) => relevantKeys.some(rk => key.toLowerCase().includes(rk)))
          .map(([key, value]) => value)
          .join(', ');
        return relevantDetails || null;
    }
  };

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/action-plans-v2/plans/${planId}/history`);
      if (response.data.success) {
        setHistory(response.data.data);
      }
    } catch (error) {
      logger.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const actionIcons = {
    created: CheckCircle,
    assignee_added: UserPlus,
    assignee_removed: UserMinus,
    approval_requested: Send,
    approval_approved: Check,
    approval_rejected: X,
    evidence_added: Upload,
    evidence_deleted: Trash2
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <History className="h-4 w-4" />
          {t.title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-gray-500">Cargando...</div>
        ) : history.length === 0 ? (
          <p className="text-sm text-gray-500">{t.noHistory}</p>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {history.map((entry) => {
              const ActionIcon = actionIcons[entry.action] || History;
              return (
                <div 
                  key={entry.id}
                  className="flex items-start gap-3 p-2 border-l-2 border-blue-200 pl-4"
                >
                  <ActionIcon className="h-4 w-4 text-gray-500 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {t.actions[entry.action] || entry.action}
                    </p>
                    <p className="text-xs text-gray-500">
                      {entry.changed_by_name} • {new Date(entry.created_at).toLocaleString()}
                    </p>
                    {formatDetails(entry.action, entry.details) && (
                      <p className="text-xs text-gray-600 mt-1 bg-gray-50 px-2 py-1 rounded">
                        {formatDetails(entry.action, entry.details)}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============== DASHBOARD SUMMARY WIDGET ==============
export const ActionPlanV2Summary = () => {
  const { language } = useLanguage();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const labels = {
    es: {
      title: 'Resumen de Colaboración',
      pendingApprovals: 'Aprobaciones Pendientes',
      myAssignments: 'Mis Tareas',
      totalEvidences: 'Total Evidencias',
      recentActivity: 'Actividad Reciente'
    },
    en: {
      title: 'Collaboration Summary',
      pendingApprovals: 'Pending Approvals',
      myAssignments: 'My Tasks',
      totalEvidences: 'Total Evidences',
      recentActivity: 'Recent Activity'
    }
  };
  const t = labels[language] || labels.es;

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const response = await axios.get('/action-plans-v2/dashboard-summary');
        if (response.data.success) {
          setSummary(response.data.data);
        }
      } catch (error) {
        logger.error('Error fetching summary:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, []);

  if (loading) return <div className="text-sm text-gray-500">Cargando...</div>;
  if (!summary) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t.title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-yellow-50 rounded-lg">
            <p className="text-2xl font-bold text-yellow-600">{summary.pending_approvals}</p>
            <p className="text-xs text-gray-600">{t.pendingApprovals}</p>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">{summary.my_assignments}</p>
            <p className="text-xs text-gray-600">{t.myAssignments}</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-600">{summary.total_evidences}</p>
            <p className="text-xs text-gray-600">{t.totalEvidences}</p>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <p className="text-2xl font-bold text-purple-600">{summary.recent_activity_count}</p>
            <p className="text-xs text-gray-600">{t.recentActivity}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default {
  AssigneesPanel,
  ApprovalPanel,
  EvidencePanel,
  HistoryPanel,
  ActionPlanV2Summary
};
